// components/membership/MembershipStatusCard.tsx — header strip showing
// the user's tier, locked price, status, started/cancelled timestamps.
//
// A-WEB-CLARITY-2: dark-theme aligned (operator palette via existing tokens;
// no layout/copy change) + explicit lifetime-lock badge. Status-badge colors
// match the retired MeBillingBadge (deleted at #183): active green / cancelled red / not-joined gray.

import { cohortWord } from "../../lib/cohortWord";
import type { MembershipStateView } from "../../lib/api";

function fmtUsd(n: number | null | undefined): string {
  if (typeof n !== "number") return "—";
  return `$${n.toFixed(2)}`;
}

function fmtTs(ts: number | null | undefined): string {
  if (!ts) return "—";
  try { return new Date(Number(ts) * 1000).toISOString().slice(0, 10); }
  catch { return String(ts); }
}

const DASH = "—";
/** A count as it came, or the dash. */
function n(v: unknown): string {
  return typeof v === "number" && Number.isFinite(v) ? String(v) : DASH;
}
/** A boolean renders its WORD; a missing one the dash (#180a standing rule). */
function word(v: unknown, yes: string, no: string): string {
  return v === true ? yes : v === false ? no : DASH;
}

interface Props {
  state: MembershipStateView;
}

export default function MembershipStatusCard({ state }: Props) {
  const m = state.membership;
  const c = state.cohort;
  const ident = state.identity;  // #124 -- the citizen id
  const isActive = m.status === "active";
  const isCancelled = m.status === "cancelled";
  const isNonMember = !m.status;

  // A-WEB-CLARITY-2 §2 — lifetime-lock indicator. price_locked is the locked
  // PRICE (number), not a boolean; the lock is live while the membership is
  // active, carries a locked price, and hasn't been forfeited.
  const showLock = isActive && typeof m.price_locked === "number" && !m.price_lock_forfeit;

  let badge: { text: string; bg: string; fg: string };
  if (isActive) badge = { text: "ACTIVE", bg: "#15803d", fg: "#fff" };
  else if (isCancelled) badge = { text: "CANCELLED", bg: "#dc2626", fg: "#fff" };
  else badge = { text: "NOT JOINED", bg: "#374151", fg: "#fff" };

  return (
    <section style={{
      border: "1px solid rgba(20, 24, 28, 0.12)",
      borderRadius: 6,
      padding: 16,
      background: "var(--color-bg-surface, #0A0A0A)",
      color: "var(--color-text-primary, #fff)",
      marginBottom: 16,
    }}>
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        marginBottom: 8,
      }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>
          {m.tier === "founding_500" ? "Founding 500" : "Membership"}
          {ident ? (
            <span
              data-testid="citz-id"
              title="state.identity.citz_id · state.identity.paid · state.identity.controller · state.identity.cohort"
              style={{ marginLeft: 10, fontSize: 12, fontFamily: "var(--font-mono, monospace)", opacity: 0.8 }}
            >
              {ident.citz_id ?? DASH}{cohortWord(ident) === "—" ? "" : ` · ${cohortWord(ident)}`}
            </span>
          ) : null}
          {/* #180b (1) -- the #171 word + the NUMBER + the citz id. */}
          {ident ? (
            <span
              data-testid="member-number"
              title="state.identity.member_number"
              style={{ marginLeft: 6, fontSize: 12, fontFamily: "var(--font-mono, monospace)", opacity: 0.8 }}
            >
              #{n(ident.member_number)}
            </span>
          ) : null}
        </h2>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          {showLock && (
            <span style={{
              padding: "2px 8px",
              background: "var(--os-backlight)",
              color: "var(--color-accent-cyan, #00F0FF)",
              border: "1px solid var(--color-accent-cyan, #00F0FF)",
              borderRadius: 3,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.05em",
            }}>
              LOCKED PRICE
            </span>
          )}
          <span style={{
            padding: "2px 8px",
            background: badge.bg,
            color: badge.fg,
            borderRadius: 3,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.05em",
          }}>
            {badge.text}
          </span>
        </div>
      </div>

      <div style={{
        display: "grid",
        gridTemplateColumns: "auto 1fr",
        gap: "4px 16px",
        fontSize: 13,
      }}>
        <span style={{ color: "var(--color-text-secondary, #888)" }}>Locked price</span>
        <strong>{fmtUsd(m.price_locked)}</strong>

        <span style={{ color: "var(--color-text-secondary, #888)" }}>Next price</span>
        <strong>{fmtUsd(m.next_price)}</strong>

        <span style={{ color: "var(--color-text-secondary, #888)" }}>Started</span>
        <span>{fmtTs(m.started_ts)}</span>

        {isCancelled && (
          <>
            <span style={{ color: "var(--color-text-secondary, #888)" }}>Cancelled</span>
            <span>{fmtTs(m.cancelled_ts)}</span>
          </>
        )}

        {/* #180b (1) -- confirmed, the price lock as a WORD, the seats. */}
        <span style={{ color: "var(--color-text-secondary, #888)" }} title="state.membership.confirmed · state.membership.confirmed_ts">Confirmed</span>
        <span data-testid="membership-confirmed">
          {m.confirmed === true ? `confirmed ${fmtTs(m.confirmed_ts)}` : word(m.confirmed, "confirmed", "not confirmed")}
        </span>

        <span style={{ color: "var(--color-text-secondary, #888)" }} title="state.membership.price_lock_forfeit">Price lock</span>
        {/* false is "not forfeited": "held" would assert a lock the reader may
            not have (price_locked null on a non-member). */}
        <span data-testid="price-lock">{word(m.price_lock_forfeit, "forfeited", "not forfeited")}</span>

        <span style={{ color: "var(--color-text-secondary, #888)" }} title="state.cohort.cohort">Cohort</span>
        <span
          data-testid="cohort-seated"
          title="state.cohort.cohort · state.cohort.active_count · state.cohort.cap · state.cohort.remaining · state.cohort.waitlist_count · state.cohort.is_full"
        >
          {c?.cohort ?? DASH} · {n(c?.active_count)} of {n(c?.cap)} seated · {n(c?.remaining)} remaining
          {" · "}waitlist {n(c?.waitlist_count)} · {word(c?.is_full, "full", "open")}
        </span>

        <span style={{ color: "var(--color-text-secondary, #888)" }} title="state.waitlist_position">Waitlist position</span>
        <strong data-testid="waitlist-position">
          {typeof state.waitlist_position === "number" ? `#${state.waitlist_position}` : DASH}
        </strong>
      </div>

      {isCancelled && (
        <div style={{
          marginTop: 12,
          padding: 8,
          background: "var(--color-bg-surface-alt, #111)",
          border: "1px solid #b45309",
          fontSize: 12,
          borderRadius: 4,
        }}>
          Reactivation will pay the full price ({fmtUsd(m.next_price)}).
          Founding-cohort price lock is forfeited on cancellation.
        </div>
      )}

      {isNonMember && !c?.is_full && (
        <div style={{
          marginTop: 12,
          padding: 8,
          background: "var(--color-bg-surface-alt, #111)",
          border: "1px solid var(--color-accent-cyan, #00F0FF)",
          fontSize: 12,
          borderRadius: 4,
        }}>
          Join the Founding 500 cohort for {fmtUsd(m.next_price)} (locked for life of membership).
        </div>
      )}

      {isNonMember && c?.is_full === true && (
        <div style={{
          marginTop: 12,
          padding: 8,
          background: "var(--color-bg-surface-alt, #111)",
          border: "1px solid var(--color-accent-red, #E02020)",
          fontSize: 12,
          borderRadius: 4,
        }}>
          The Founding 500 cohort is full. You'll be added to the waitlist.
        </div>
      )}
    </section>
  );
}
